import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { StarField } from "./StarField";
import { Sun } from "./Sun";
import { Planet } from "./Planet";
import { Moon } from "./Moon";
import { OrbitPath } from "./OrbitPath";
import { ReferencePlane } from "./ReferencePlane";
import { useSimulationClock } from "../../hooks/useSimulationClock";
import { getLiveSimulationDate } from "../../store/liveClock";
import { useSceneStore } from "../../store/sceneStore";
import { useSettingsStore } from "../../store/settingsStore";
import { catalogById, getMoons, getPlanets } from "../../data/catalog";
import { dateToJulianDate } from "../../astronomy/julianDate";
import { calculateAbsolutePosition } from "../../astronomy/ephemeris";
import { getOrbitSampleCount } from "../../utils/performance";
import { toScenePosition, toSceneRadius, TRUE_SCALE } from "../../utils/scaling";
import { earth } from "../../data/planets";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";

/** Distância para o diâmetro ocupar `fraction` da altura da viewport. */
function distanceForViewportFraction(
  radiusScene: number,
  fovDeg: number,
  fraction: number,
): number {
  const fov = THREE.MathUtils.degToRad(fovDeg);
  const safeRadius = Math.max(radiusScene, 1e-9);
  const safeFraction = Math.max(0.01, Math.min(0.95, fraction));
  return safeRadius / (safeFraction * Math.tan(fov / 2));
}

function getEarthFraming(fovDeg = 45, fraction = 0.2) {
  const jd = dateToJulianDate(new Date());
  const posKm = calculateAbsolutePosition(earth, jd, catalogById);
  const [x, y, z] = toScenePosition(posKm, TRUE_SCALE);
  const radius = toSceneRadius(earth.physical.meanRadiusKm, TRUE_SCALE);
  const distance = distanceForViewportFraction(radius, fovDeg, fraction);
  const dir = new THREE.Vector3(0.55, 0.35, 0.75).normalize();
  return {
    target: [x, y, z] as [number, number, number],
    position: [
      x + dir.x * distance,
      y + dir.y * distance,
      z + dir.z * distance,
    ] as [number, number, number],
  };
}

const initialEarthFrame = getEarthFraming(45, 0.2);

function SimulationTicker() {
  useSimulationClock();
  return null;
}

function CameraRig() {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const { camera } = useThree();
  const cameraMode = useSceneStore((s) => s.cameraMode);
  const focusedBodyId = useSceneStore((s) => s.focusedBodyId);
  const selectedBodyId = useSceneStore((s) => s.selectedBodyId);
  const focusVersion = useSceneStore((s) => s.focusVersion);
  const focusViewportFraction = useSceneStore((s) => s.focusViewportFraction);
  const scale = useSettingsStore((s) => s.scale);
  const framingRef = useRef({
    active: true,
    version: 0,
    desiredDistance: 1,
  });
  const tempTarget = useRef(new THREE.Vector3());
  const tempDir = useRef(new THREE.Vector3());
  const tempDesired = useRef(new THREE.Vector3());
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current && controlsRef.current) {
      didInit.current = true;
      controlsRef.current.target.set(...initialEarthFrame.target);
      camera.position.set(...initialEarthFrame.position);
      controlsRef.current.update();
    }
  }, [camera]);

  useEffect(() => {
    if (cameraMode === "heliocentric") {
      camera.position.set(0, 80, 220);
      if (controlsRef.current) {
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
      }
    }
  }, [cameraMode, camera]);

  useEffect(() => {
    const targetId = focusedBodyId ?? selectedBodyId;
    if (!targetId) return;
    const body = catalogById.get(targetId);
    if (!body) return;

    const radius = toSceneRadius(body.physical.meanRadiusKm, scale);
    const perspective = camera as THREE.PerspectiveCamera;
    framingRef.current = {
      active: true,
      version: focusVersion,
      desiredDistance: distanceForViewportFraction(
        radius,
        perspective.fov,
        focusViewportFraction,
      ),
    };
  }, [
    focusVersion,
    focusedBodyId,
    selectedBodyId,
    focusViewportFraction,
    scale,
    camera,
  ]);

  useFrame(() => {
    if (!controlsRef.current) return;

    const targetId = focusedBodyId ?? selectedBodyId ?? "earth";
    const body = catalogById.get(targetId);
    if (!body) return;

    const posKm = calculateAbsolutePosition(
      body,
      dateToJulianDate(getLiveSimulationDate()),
      catalogById,
    );
    const [x, y, z] = toScenePosition(posKm, scale);
    tempTarget.current.set(x, y, z);

    const follow =
      cameraMode === "follow" || cameraMode === "planetocentric";

    if (follow || framingRef.current.active) {
      controlsRef.current.target.lerp(tempTarget.current, 0.18);

      const framing = framingRef.current;
      if (framing.active) {
        tempDir.current
          .copy(camera.position)
          .sub(controlsRef.current.target);
        if (tempDir.current.lengthSq() < 1e-12) {
          tempDir.current.set(0.55, 0.35, 0.75);
        }
        tempDir.current.normalize();
        tempDesired.current
          .copy(tempTarget.current)
          .addScaledVector(tempDir.current, framing.desiredDistance);

        camera.position.lerp(tempDesired.current, 0.22);

        const dist = camera.position.distanceTo(tempTarget.current);
        if (
          Math.abs(dist - framing.desiredDistance) / framing.desiredDistance <
          0.03
        ) {
          framing.active = false;
        }
      } else if (follow) {
        tempDir.current
          .copy(camera.position)
          .sub(controlsRef.current.target);
        const currentDist = Math.max(tempDir.current.length(), 1e-9);
        tempDir.current.normalize();
        tempDesired.current
          .copy(tempTarget.current)
          .addScaledVector(tempDir.current, currentDist);
        camera.position.lerp(tempDesired.current, 0.18);
      }

      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.08}
      minDistance={1e-7}
      maxDistance={12000}
      makeDefault
    />
  );
}

function MoonOrbitFollower({
  planetId,
  children,
}: {
  planetId: string;
  children: React.ReactNode;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const scale = useSettingsStore((s) => s.scale);

  useFrame(() => {
    const body = catalogById.get(planetId);
    if (!body || !groupRef.current) return;
    const posKm = calculateAbsolutePosition(
      body,
      dateToJulianDate(getLiveSimulationDate()),
      catalogById,
    );
    const [x, y, z] = toScenePosition(posKm, scale);
    groupRef.current.position.set(x, y, z);
  });

  return <group ref={groupRef}>{children}</group>;
}

function BodiesLayer() {
  const scale = useSettingsStore((s) => s.scale);
  const performanceMode = useSettingsStore((s) => s.performanceMode);
  const showOrbits = useSceneStore((s) => s.showOrbits);
  const showPlanets = useSceneStore((s) => s.showPlanets);
  const showMoons = useSceneStore((s) => s.showMoons);
  const selectedBodyId = useSceneStore((s) => s.selectedBodyId);
  const focusedBodyId = useSceneStore((s) => s.focusedBodyId);
  const focusBody = useSceneStore((s) => s.focusBody);

  const planets = useMemo(() => getPlanets(), []);
  const moons = useMemo(() => getMoons(), []);

  const orbitMoonParentId = useMemo(() => {
    const id = focusedBodyId ?? selectedBodyId;
    if (!id) return null;
    const body = catalogById.get(id);
    if (!body) return null;
    if (body.type === "planet") return body.id;
    if (body.type === "moon") return body.parentId;
    return null;
  }, [focusedBodyId, selectedBodyId]);

  return (
    <group name="solar-system">
      <Sun />

      {showOrbits &&
        showPlanets &&
        planets.map((planet) =>
          planet.orbit ? (
            <OrbitPath
              key={`orbit-${planet.id}`}
              orbit={planet.orbit}
              scale={scale}
              color={planet.color}
              sampleCount={getOrbitSampleCount("planet", performanceMode)}
            />
          ) : null,
        )}

      {showPlanets &&
        planets.map((planet) => {
          const children = moons.filter((m) => m.parentId === planet.id);
          const showMoonOrbits =
            showOrbits &&
            showMoons &&
            orbitMoonParentId === planet.id;

          return (
            <group key={planet.id} name={`${planet.id}-system`}>
              <Planet
                definition={planet}
                scale={scale}
                selected={selectedBodyId === planet.id}
                onSelect={(id) => focusBody(id, 0.5)}
              />
              {showMoonOrbits && (
                <MoonOrbitFollower planetId={planet.id}>
                  {children.map((moon) =>
                    moon.orbit ? (
                      <OrbitPath
                        key={`moon-orbit-${moon.id}`}
                        orbit={moon.orbit}
                        scale={scale}
                        color="#8899aa"
                        sampleCount={
                          performanceMode === "performance"
                            ? 32
                            : getOrbitSampleCount("moon", performanceMode)
                        }
                      />
                    ) : null,
                  )}
                </MoonOrbitFollower>
              )}
            </group>
          );
        })}

      {showMoons &&
        moons.map((moon) => (
          <Moon
            key={moon.id}
            definition={moon}
            scale={scale}
            selected={selectedBodyId === moon.id}
            onSelect={(id) => focusBody(id, 0.5)}
          />
        ))}
    </group>
  );
}

function SceneContent() {
  const showReferencePlane = useSceneStore((s) => s.showReferencePlane);
  const performanceMode = useSettingsStore((s) => s.performanceMode);
  const starCount =
    performanceMode === "performance"
      ? 1500
      : performanceMode === "quality"
        ? 6000
        : 4000;

  return (
    <>
      <color attach="background" args={["#030712"]} />
      <ambientLight intensity={0.08} />
      <SimulationTicker />
      <CameraRig />
      <StarField count={starCount} />
      <ReferencePlane visible={showReferencePlane} size={5000} />
      <BodiesLayer />
    </>
  );
}

export function SolarSystemScene() {
  return (
    <div className="tls-astro-canvas-wrap">
      <Canvas
        camera={{
          position: initialEarthFrame.position,
          fov: 45,
          near: 0.0001,
          far: 50000,
        }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, logarithmicDepthBuffer: true }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.setClearColor(new THREE.Color("#030712"));
        }}
      >
        <SceneContent />
      </Canvas>
    </div>
  );
}
